import { useState, useEffect } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import api from '@/lib/api';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { 
  Activity, AlertTriangle, Shield, Clock, Users, Bot, 
  FileText, TrendingUp, TrendingDown, Minus, RefreshCw,
  ChevronDown, Search, Filter, Bell, Settings, Loader2,
  Zap, Database, Server, Eye
} from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { CustomSelect } from '@/components/ui/custom-select';

export default function Observability() {
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin';
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('dashboard');
  const [metrics, setMetrics] = useState(null);
  const [alerts, setAlerts] = useState([]);
  const [logs, setLogs] = useState([]);
  const [guardrails, setGuardrails] = useState(null);
  const [showGuardrailsModal, setShowGuardrailsModal] = useState(false);
  const [logFilters, setLogFilters] = useState({ event_type: '', level: '', limit: 50 });
  const [refreshing, setRefreshing] = useState(false);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      const [metricsRes, alertsRes] = await Promise.all([
        api.get('/observability/metrics'),
        api.get('/observability/alerts')
      ]);
      setMetrics(metricsRes.data);
      setAlerts(alertsRes.data);
      
      if (isAdmin) {
        const guardrailsRes = await api.get('/observability/guardrails');
        setGuardrails(guardrailsRes.data);
      }
    } catch (err) {
      console.error('Failed to load observability data:', err);
    } finally {
      setLoading(false);
    }
  };

  const loadLogs = async () => {
    try {
      const params = new URLSearchParams();
      if (logFilters.event_type) params.append('event_type', logFilters.event_type);
      if (logFilters.level) params.append('level', logFilters.level);
      params.append('limit', logFilters.limit.toString());
      
      const res = await api.get(`/observability/logs?${params}`);
      setLogs(res.data);
    } catch (err) {
      console.error('Failed to load logs:', err);
    }
  };

  useEffect(() => {
    if (activeTab === 'logs') {
      loadLogs();
    }
  }, [activeTab, logFilters]);

  const refreshData = async () => {
    setRefreshing(true);
    await loadData();
    setRefreshing(false);
  };

  const saveGuardrails = async (config) => {
    try {
      await api.put('/observability/guardrails', config);
      setGuardrails(config);
      setShowGuardrailsModal(false);
      alert('Guardrails saved successfully');
    } catch (err) {
      alert('Failed to save guardrails: ' + (err.response?.data?.detail || err.message));
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Loader2 className="w-8 h-8 animate-spin text-[#98139C]" />
      </div>
    );
  }

  return (
    <div className="p-8 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-[#30302F] dark:text-white flex items-center gap-3">
            <Activity className="text-[#98139C]" />
            System Observability
          </h1>
          <p className="text-[#30302F]/80 dark:text-white/80 mt-1">
            Monitor system health, logs, metrics, and guardrails
          </p>
        </div>
        <div className="flex items-center gap-3">
          <button
            onClick={refreshData}
            disabled={refreshing}
            className="flex items-center gap-2 px-5 h-9 border-2 border-[#4F0077] dark:border-[#98139C] text-[#4F0077] dark:text-[#d87bdb] rounded-full font-bold text-sm hover:bg-[#f9f5fa] dark:hover:bg-white/5 transition-colors"
            aria-label="Refresh observability data"
          >
            <RefreshCw className={`w-4 h-4 ${refreshing ? 'animate-spin' : ''}`} aria-hidden="true" />
            Refresh
          </button>
          {isAdmin && (
            <button
              onClick={() => setShowGuardrailsModal(true)}
              className="flex items-center gap-2 px-5 h-9 bg-[#98139C] text-white rounded-full font-bold text-sm hover:bg-[#7a0f7d] transition-colors"
              aria-label="Configure guardrails"
            >
              <Shield className="w-4 h-4" aria-hidden="true" />
              Guardrails
            </button>
          )}
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-2 border-b border-[#e3e3e3] dark:border-white/10">
        {['dashboard', 'logs', 'alerts'].map(tab => (
          <button
            key={tab}
            onClick={() => setActiveTab(tab)}
            className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
              activeTab === tab
                ? 'border-[#98139C] text-[#98139C]'
                : 'border-transparent text-[#30302F]/80 dark:text-white/80 hover:text-[#30302F] dark:hover:text-white'
            }`}
          >
            {tab.charAt(0).toUpperCase() + tab.slice(1)}
          </button>
        ))}
      </div>

      {/* Active Alerts Banner */}
      {alerts.length > 0 && (
        <div className="bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 p-4">
          <div className="flex items-center gap-3">
            <AlertTriangle className="w-5 h-5 text-amber-600" />
            <span className="font-medium text-amber-800 dark:text-amber-200">
              {alerts.length} Active Alert{alerts.length > 1 ? 's' : ''}
            </span>
          </div>
          <div className="mt-2 space-y-1">
            {alerts.map((alert, i) => (
              <p key={i} className="text-sm text-amber-700 dark:text-amber-300">
                • {alert.message}
              </p>
            ))}
          </div>
        </div>
      )}

      {/* Dashboard Tab */}
      {activeTab === 'dashboard' && metrics && (
        <div className="space-y-6">
          {/* Metrics Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            <MetricCard
              icon={Users}
              label="Active Users Today"
              value={metrics.users?.active_today || 0}
              subtext={`${metrics.users?.total || 0} total users`}
              color="blue"
            />
            <MetricCard
              icon={Bot}
              label="Agent Runs Today"
              value={metrics.agents?.total_runs_today || 0}
              subtext={`${metrics.agents?.failed_runs_today || 0} failed`}
              color="purple"
            />
            <MetricCard
              icon={Zap}
              label="Tokens Used Today"
              value={formatNumber(metrics.tokens?.used_today || 0)}
              subtext="AI token consumption"
              color="green"
            />
            <MetricCard
              icon={AlertTriangle}
              label="Errors Today"
              value={metrics.api?.errors_today || 0}
              subtext={`${metrics.api?.warnings_today || 0} warnings`}
              color={metrics.api?.errors_today > 0 ? "red" : "gray"}
            />
          </div>

          {/* Performance Stats */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="bg-white dark:bg-white/5 p-5 border border-[#e3e3e3] dark:border-white/10 hover:border-[#98139C] dark:hover:border-[#98139C] transition-colors">
              <h3 className="text-sm font-medium text-[#30302F]/80 dark:text-white/80 mb-3">Response Performance</h3>
              <div className="text-3xl font-bold text-[#30302F] dark:text-white">
                {metrics.agents?.avg_latency_ms || 0}ms
              </div>
              <p className="text-sm text-[#30302F]/70 dark:text-white/70 mt-1">Avg agent response time</p>
            </div>
            <div className="bg-white dark:bg-white/5 p-5 border border-[#e3e3e3] dark:border-white/10 hover:border-[#98139C] dark:hover:border-[#98139C] transition-colors">
              <h3 className="text-sm font-medium text-[#30302F]/80 dark:text-white/80 mb-3">Work Items Today</h3>
              <div className="flex items-end gap-4">
                <div>
                  <div className="text-3xl font-bold text-[#30302F] dark:text-white">
                    {metrics.work_items?.stories_created_today || 0}
                  </div>
                  <p className="text-sm text-[#30302F]/70 dark:text-white/70">Created</p>
                </div>
                <div>
                  <div className="text-3xl font-bold text-green-600">
                    {metrics.work_items?.stories_completed_today || 0}
                  </div>
                  <p className="text-sm text-[#30302F]/70 dark:text-white/70">Completed</p>
                </div>
              </div>
            </div>
            <div className="bg-white dark:bg-white/5 p-5 border border-[#e3e3e3] dark:border-white/10 hover:border-[#98139C] dark:hover:border-[#98139C] transition-colors">
              <h3 className="text-sm font-medium text-[#30302F]/80 dark:text-white/80 mb-3">API Requests</h3>
              <div className="text-3xl font-bold text-[#30302F] dark:text-white">
                {metrics.api?.requests_today || 0}
              </div>
              <p className="text-sm text-[#30302F]/70 dark:text-white/70 mt-1">Total requests today</p>
            </div>
          </div>

          {/* Guardrails Status (Admin only) */}
          {isAdmin && guardrails && (
            <div className="bg-white dark:bg-white/5 p-5 border border-[#e3e3e3] dark:border-white/10">
              <h3 className="text-lg font-semibold text-[#30302F] dark:text-white mb-4 flex items-center gap-2">
                <Shield className="w-5 h-5 text-[#98139C]" />
                Guardrails Status
              </h3>
              <div className="grid grid-cols-4 gap-4">
                <div>
                  <p className="text-sm text-[#30302F]/80 dark:text-white/80">Max Tokens/Run</p>
                  <p className="text-lg font-semibold text-[#30302F] dark:text-white">
                    {guardrails.token_limits?.max_tokens_per_run || 'N/A'}
                  </p>
                </div>
                <div>
                  <p className="text-sm text-[#30302F]/80 dark:text-white/80">Daily User Limit</p>
                  <p className="text-lg font-semibold text-[#30302F] dark:text-white">
                    {formatNumber(guardrails.token_limits?.max_tokens_per_day_per_user || 0)}
                  </p>
                </div>
                <div>
                  <p className="text-sm text-[#30302F]/80 dark:text-white/80">Rate Limit (Hourly)</p>
                  <p className="text-lg font-semibold text-[#30302F] dark:text-white">
                    {guardrails.rate_limits?.max_runs_per_hour_per_user || 'N/A'} runs
                  </p>
                </div>
                <div>
                  <p className="text-sm text-[#30302F]/80 dark:text-white/80">Content Filter</p>
                  <Badge className={guardrails.content_filter?.enabled ? 'bg-green-500' : 'bg-gray-500'}>
                    {guardrails.content_filter?.enabled ? 'Enabled' : 'Disabled'}
                  </Badge>
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Logs Tab */}
      {activeTab === 'logs' && (
        <div className="space-y-4">
          {/* Filters */}
          <div className="flex gap-4">
            <CustomSelect
              value={logFilters.event_type}
              onChange={e => setLogFilters({...logFilters, event_type: e.target.value})}
              options={[
                { value: '', label: 'All Events' },
                { value: 'user.login', label: 'User Login' },
                { value: 'story.created', label: 'Story Created' },
                { value: 'story.updated', label: 'Story Updated' },
                { value: 'story.moved', label: 'Story Moved' },
                { value: 'sprint.created', label: 'Sprint Created' },
                { value: 'sprint.updated', label: 'Sprint Updated' },
                { value: 'intake.submitted', label: 'Intake Submitted' },
                { value: 'approval.approved', label: 'Approval Approved' },
                { value: 'agent.triggered', label: 'Agent Run' },
                { value: 'agent.toggled', label: 'Agent Toggled' },
                { value: 'report.generated', label: 'Report Generated' },
                { value: 'report.updated', label: 'Report Updated' },
                { value: 'settings_changed', label: 'Settings Changed' },
                { value: 'guardrail_blocked', label: 'Guardrail Blocked' },
              ]}
              aria-label="Filter by event type"
            />
            <CustomSelect
              value={logFilters.level}
              onChange={e => setLogFilters({...logFilters, level: e.target.value})}
              options={[
                { value: '', label: 'All Levels' },
                { value: 'INFO', label: 'Info' },
                { value: 'WARNING', label: 'Warning' },
                { value: 'ERROR', label: 'Error' },
              ]}
              aria-label="Filter by log level"
            />
          </div>

          {/* Logs Table */}
          <div className="bg-white dark:bg-white/5 border border-[#e3e3e3] dark:border-white/10 overflow-hidden">
            <table className="w-full" aria-label="Audit logs">
              <thead className="bg-[#f9f5fa] dark:bg-white/5 border-b border-[#e3e3e3] dark:border-white/10">
                <tr>
                  <th scope="col" className="text-left px-4 py-3 text-sm font-semibold text-[#30302F] dark:text-white">Timestamp</th>
                  <th scope="col" className="text-left px-4 py-3 text-sm font-semibold text-[#30302F] dark:text-white">Level</th>
                  <th scope="col" className="text-left px-4 py-3 text-sm font-semibold text-[#30302F] dark:text-white">Event</th>
                  <th scope="col" className="text-left px-4 py-3 text-sm font-semibold text-[#30302F] dark:text-white">User</th>
                  <th scope="col" className="text-left px-4 py-3 text-sm font-semibold text-[#30302F] dark:text-white">Details</th>
                </tr>
              </thead>
              <tbody>
                {logs.map((log, i) => (
                  <tr key={i} className="border-b border-[#e3e3e3] dark:border-white/5 last:border-0">
                    <td className="px-4 py-3 text-sm text-[#30302F]/80 dark:text-white/80 font-mono">
                      {new Date(log.timestamp).toLocaleString()}
                    </td>
                    <td className="px-4 py-3">
                      <Badge className={
                        log.level === 'ERROR' ? 'bg-red-500' :
                        log.level === 'WARNING' ? 'bg-amber-500' : 'bg-blue-500'
                      }>
                        {log.level}
                      </Badge>
                    </td>
                    <td className="px-4 py-3 text-sm text-[#30302F] dark:text-white">
                      {log.event_type?.replace(/[_.]/g, ' ')}
                    </td>
                    <td className="px-4 py-3 text-sm text-[#30302F]/80 dark:text-white/80 truncate max-w-[150px]">
                      {log.user_id?.slice(0, 8)}...
                    </td>
                    <td className="px-4 py-3 text-sm text-[#30302F]/80 dark:text-white/80 truncate max-w-[300px]">
                      {log.details?.message || JSON.stringify(log.details).slice(0, 50)}
                    </td>
                  </tr>
                ))}
                {logs.length === 0 && (
                  <tr>
                    <td colSpan={5} className="px-4 py-8 text-center text-[#30302F]/60 dark:text-white/60">
                      No logs found
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Alerts Tab */}
      {activeTab === 'alerts' && (
        <div className="space-y-4">
          {alerts.length === 0 ? (
            <div className="bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 p-8 text-center">
              <div className="w-12 h-12 rounded-full bg-green-100 dark:bg-green-800 flex items-center justify-center mx-auto mb-3">
                <Activity className="w-6 h-6 text-green-600 dark:text-green-400" />
              </div>
              <h3 className="text-lg font-semibold text-green-800 dark:text-green-200">All Systems Operational</h3>
              <p className="text-green-600 dark:text-green-400 mt-1">No active alerts at this time</p>
            </div>
          ) : (
            <div className="space-y-3">
              {alerts.map((alert, i) => (
                <div
                  key={i}
                  className={`p-4 border ${
                    alert.severity === 'high'
                      ? 'bg-red-50 dark:bg-red-900/20 border-red-200 dark:border-red-800'
                      : 'bg-amber-50 dark:bg-amber-900/20 border-amber-200 dark:border-amber-800'
                  }`}
                >
                  <div className="flex items-start gap-3">
                    <AlertTriangle className={`w-5 h-5 mt-0.5 ${
                      alert.severity === 'high' ? 'text-red-600' : 'text-amber-600'
                    }`} />
                    <div className="flex-1">
                      <div className="flex items-center gap-2">
                        <Badge className={alert.severity === 'high' ? 'bg-red-500' : 'bg-amber-500'}>
                          {alert.severity.toUpperCase()}
                        </Badge>
                        <span className="text-sm text-[#30302F]/70 dark:text-white/70">
                          {alert.type?.replace(/_/g, ' ')}
                        </span>
                      </div>
                      <p className={`mt-1 ${
                        alert.severity === 'high' ? 'text-red-800 dark:text-red-200' : 'text-amber-800 dark:text-amber-200'
                      }`}>
                        {alert.message}
                      </p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Guardrails Modal */}
      <Dialog open={showGuardrailsModal} onOpenChange={setShowGuardrailsModal}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Shield className="w-5 h-5 text-[#98139C]" />
              Agent Guardrails Configuration
            </DialogTitle>
          </DialogHeader>
          {guardrails && (
            <GuardrailsForm guardrails={guardrails} onSave={saveGuardrails} onCancel={() => setShowGuardrailsModal(false)} />
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

function MetricCard({ icon: Icon, label, value, subtext, color }) {
  const colorClasses = {
    blue: 'bg-blue-500 dark:bg-blue-500/80',
    purple: 'bg-[#98139C] dark:bg-[#98139C]/80',
    green: 'bg-green-500 dark:bg-green-500/80',
    red: 'bg-red-500 dark:bg-red-500/80',
    gray: 'bg-gray-500 dark:bg-gray-500/80'
  };

  return (
    <div className="bg-white dark:bg-white/5 p-5 border border-[#e3e3e3] dark:border-white/10 hover:border-[#98139C] dark:hover:border-[#98139C] transition-colors">
      <div className="flex items-center gap-3 mb-3">
        <div className={`w-9 h-9 rounded-lg ${colorClasses[color]} flex items-center justify-center`}>
          <Icon className="w-4 h-4 text-white" />
        </div>
        <span className="text-sm font-medium text-[#30302F]/80 dark:text-white/80">{label}</span>
      </div>
      <div className="text-3xl font-bold text-[#30302F] dark:text-white">{value}</div>
      <p className="text-sm text-[#30302F]/70 dark:text-white/70 mt-1">{subtext}</p>
    </div>
  );
}

function GuardrailsForm({ guardrails, onSave, onCancel }) {
  const [config, setConfig] = useState(guardrails);

  const updateTokenLimits = (key, value) => {
    setConfig({
      ...config,
      token_limits: { ...config.token_limits, [key]: parseInt(value) || 0 }
    });
  };

  const updateRateLimits = (key, value) => {
    setConfig({
      ...config,
      rate_limits: { ...config.rate_limits, [key]: parseInt(value) || 0 }
    });
  };

  return (
    <div className="space-y-6 py-4">
      {/* Token Limits */}
      <div>
        <h4 className="font-medium text-[#30302F] dark:text-white mb-3">Token Limits</h4>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="text-xs text-[#30302F]/80 dark:text-white/80">Max per run</label>
            <Input
              type="number"
              value={config.token_limits?.max_tokens_per_run || ''}
              onChange={e => updateTokenLimits('max_tokens_per_run', e.target.value)}
            />
          </div>
          <div>
            <label className="text-xs text-[#30302F]/80 dark:text-white/80">Daily per user</label>
            <Input
              type="number"
              value={config.token_limits?.max_tokens_per_day_per_user || ''}
              onChange={e => updateTokenLimits('max_tokens_per_day_per_user', e.target.value)}
            />
          </div>
        </div>
      </div>

      {/* Rate Limits */}
      <div>
        <h4 className="font-medium text-[#30302F] dark:text-white mb-3">Rate Limits</h4>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="text-xs text-[#30302F]/80 dark:text-white/80">Max runs/hour</label>
            <Input
              type="number"
              value={config.rate_limits?.max_runs_per_hour_per_user || ''}
              onChange={e => updateRateLimits('max_runs_per_hour_per_user', e.target.value)}
            />
          </div>
          <div>
            <label className="text-xs text-[#30302F]/80 dark:text-white/80">Max runs/day</label>
            <Input
              type="number"
              value={config.rate_limits?.max_runs_per_day_per_user || ''}
              onChange={e => updateRateLimits('max_runs_per_day_per_user', e.target.value)}
            />
          </div>
        </div>
      </div>

      {/* Content Filter */}
      <div>
        <h4 className="font-medium text-[#30302F] dark:text-white mb-3">Content Filter</h4>
        <div className="space-y-2">
          <label className="flex items-center gap-2">
            <input
              type="checkbox"
              checked={config.content_filter?.enabled || false}
              onChange={e => setConfig({...config, content_filter: {...config.content_filter, enabled: e.target.checked}})}
              className="w-4 h-4 rounded accent-[#98139C]"
            />
            <span className="text-sm text-[#30302F] dark:text-white">Enable content filtering</span>
          </label>
          <label className="flex items-center gap-2">
            <input
              type="checkbox"
              checked={config.content_filter?.block_pii || false}
              onChange={e => setConfig({...config, content_filter: {...config.content_filter, block_pii: e.target.checked}})}
              className="w-4 h-4 rounded accent-[#98139C]"
            />
            <span className="text-sm text-[#30302F] dark:text-white">Block PII (SSN, credit cards)</span>
          </label>
        </div>
      </div>

      {/* Approval Gates */}
      <div>
        <h4 className="font-medium text-[#30302F] dark:text-white mb-3">Approval Gates</h4>
        <label className="flex items-center gap-2">
          <input
            type="checkbox"
            checked={config.approval_gates?.require_approval_for_actions || false}
            onChange={e => setConfig({...config, approval_gates: {...config.approval_gates, require_approval_for_actions: e.target.checked}})}
            className="w-4 h-4"
          />
          <span className="text-sm text-[#30302F] dark:text-white">Require approval for sensitive actions</span>
        </label>
      </div>

      <div className="flex gap-3 pt-4 border-t border-[#e3e3e3] dark:border-white/10">
        <button
          onClick={() => onSave(config)}
          className="flex-1 bg-[#98139C] hover:bg-[#7a0f7d] text-white rounded-full font-bold text-sm h-9 transition-colors"
        >
          Save Guardrails
        </button>
        <button
          onClick={onCancel}
          className="px-6 border-2 border-[#e3e3e3] dark:border-white/10 text-[#30302F] dark:text-white rounded-full font-bold text-sm h-9 hover:bg-gray-50 dark:hover:bg-white/5 transition-colors"
        >
          Cancel
        </button>
      </div>
    </div>
  );
}

function formatNumber(num) {
  if (num >= 1000000) return (num / 1000000).toFixed(1) + 'M';
  if (num >= 1000) return (num / 1000).toFixed(1) + 'K';
  return num.toString();
}
